"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";

import { cn } from "@/util";
import type { Project } from "@/global";

export const ProjectCard = ({
  title,
  subtitle,
  img,
  url,
  tech,
}: Omit<Project, "id">) => {
  const [showDesc, setShowDesc] = useState(false);

  return (
    <div
      onClick={() => setShowDesc((prev) => !prev)}
      className={cn(
        "space-y-3 cursor-pointer group",
        showDesc && "cursor-alias"
      )}
    >
      <div className="relative rounded-3xl border shadow-2xl overflow-hidden bg-white/5 p-4 md:p-7 flex justify-center items-center">
        <div
          className={cn(
            "opacity-0 transition-all absolute bottom-0 w-full z-30 left-0 bg-gradient-to-t from-black to-transparent h-3/4 duration-300",
            showDesc && "opacity-100"
          )}
        >
          <div className="absolute bottom-4 md:bottom-6 left-4 md:left-6">
            <Link
              href={url}
              rel="noreferrer"
              target="_blank"
              className="text-themeBlue capitalize"
            >
              view
            </Link>
            <div className="first-letter:capitalize mt-2 text-sm select-none mr-2">
              {subtitle}
            </div>
          </div>
        </div>
        <SmoothImage img={img} title={title} showDesc={showDesc} />
      </div>
      <div className="flex justify-between items-center">
        <div className="text-base md:text-xl capitalize text-stone-400">
          {title}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {tech.map((e) => (
            <div key={e} className="tag">
              {e}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export interface SmoothImageProps {
  img: string;
  title: string;
  showDesc?: boolean;
}

export const SmoothImage = ({ img, title, showDesc }: SmoothImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div className="relative w-full h-40 md:h-72 overflow-hidden rounded-3xl">
      {!isLoaded ? (
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-800 via-neutral-700 to-neutral-800 animate-pulse" />
      ) : null}
      <Image
        src={`/projects/${img}.png`}
        alt={title}
        width={600}
        height={400}
        priority={false}
        loading="lazy"
        placeholder="blur"
        blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVQIW2NkYGD4DwABAwEAeScW6QAAAABJRU5ErkJggg=="
        onLoadingComplete={() => setIsLoaded(true)}
        className={cn(
          "w-full h-full object-contain transition-all duration-700 ease-in-out",
          showDesc && "opacity-60",
          isLoaded
            ? "blur-0 scale-100 opacity-100"
            : "blur-md scale-105 opacity-70"
        )}
      />
    </div>
  );
};
